import { inject, Service } from "@angular/core";
import { filter } from "rxjs";

import {
  BenchmarkEntryInfoType,
  CardEntryInfoType,
  DataEntryDetails,
  MainResponseModel,
  ReadingOperationStrategicTask,
} from "../models";
import { MainService } from "./main-service";

@Service()
export class DataentryApiService {
  private readonly _mainService = inject(MainService);

  public getMergedInfo({ itemId, periodId, itemType }: { itemId: number; itemType: number; periodId: number }) {
    const url = "dataentry/getMergedInfo";
    const qs = new URLSearchParams({
      itemId: `${itemId}`,
      itemType: `${itemType}`,
      periodId: `${periodId}`,
    });

    return this._mainService
      .sendRequest<MainResponseModel<CardEntryInfoType>>({
        method: "GET",
        url: `${url}?${qs}`,
      })
      .pipe(filter((res) => !res.inError));
  }

  public getInfov2({ itemId, periodId, itemType }: { itemId: number; itemType: number; periodId: number }) {
    const url = "dataentry/getInfov2";
    const qs = new URLSearchParams({
      itemId: `${itemId}`,
      itemType: `${itemType}`,
      periodId: `${periodId}`,
    });

    return this._mainService
      .sendRequest<MainResponseModel<CardEntryInfoType>>({
        method: "GET",
        url: `${url}?${qs}`,
      })
      .pipe(filter((res) => !res.inError));
  }

  public getBenchmarkInfo({ itemId, itemType, year }: { itemId: number; itemType: number; year: number }) {
    const url = "dataentry/getInfov2";
    const qs = new URLSearchParams({
      itemId: `${itemId}`,
      itemType: `${itemType}`,
      year: `${year}`,
    });

    return this._mainService.sendRequest<MainResponseModel<BenchmarkEntryInfoType>>({
      method: "GET",
      url: `${url}?${qs}`,
    });
  }

  public updateBenchmarkInfo(data: BenchmarkEntryInfoType) {
    return this._mainService.sendRequest<MainResponseModel>({
      method: "POST",
      url: "dataentry/updateBenchmarkInfo",
      data: data,
    });
  }

  public getByKpiId(kpiId: number) {
    const url = `dataentry/getByKpiIdv3/${kpiId}`;

    return this._mainService.sendRequest<MainResponseModel<DataEntryDetails>>({
      method: "GET",
      url: `${url}`,
    });
  }

  public getByKpiIdv2<T>(kpiId: number) {
    const url = `dataentry/getByKpiIdv2/${kpiId}`;

    return this._mainService.sendRequest<MainResponseModel<T>>({
      method: "GET",
      url: `${url}`,
    });
  }

  public getAnalysisDifferencesInfo(itemId: number, periodId: number, itemType: number) {
    const url = `dataentry/getInfov2?itemId=${itemId}&periodId=${periodId}&itemType=${itemType}&includeFile=false`;

    return this._mainService.sendRequest<MainResponseModel<CardEntryInfoType>>({
      method: "GET",
      url: `${url}`,
    });
  }

  public updateCardDataEntryInfo({
    itemId,
    periodId,
    itemType,
    data,
  }: {
    itemId: number;
    itemType: number;
    periodId: number;
    data: CardEntryInfoType;
  }) {
    const url = "dataentry/updateInfo";
    const qs = new URLSearchParams({
      itemId: `${itemId}`,
      itemType: `${itemType}`,
      periodId: `${periodId}`,
    });

    return this._mainService
      .sendRequest<MainResponseModel>({
        method: "POST",
        url: `${url}?${qs}`,
        data: data,
      })
      .pipe(filter((res) => !res.inError));
  }

  public saveKpiDataEntry(dataEntry: DataEntryDetails) {
    return this._mainService.sendRequest<MainResponseModel>({
      method: "PUT",
      url: `dataentry/updateKpiEntryV3`,
      data: dataEntry,
    });
  }

  public getByActivityId(activityId: number) {
    return this._mainService.sendRequest<MainResponseModel<DataEntryDetails>>({
      method: "GET",
      url: `dataentry/getByActivityId/${activityId}`,
    });
  }

  public saveActivityDataEntry(dataEntry: DataEntryDetails) {
    return this._mainService.sendRequest<MainResponseModel>({
      method: "PUT",
      url: `dataentry/updateActivityEntry`,
      data: dataEntry,
    });
  }

  public getOperationStrategicTasks(readingId: number, type: "C" | "M" = "C") {
    return this._mainService.sendRequest<MainResponseModel<ReadingOperationStrategicTask[]>>({
      method: "GET",
      url: `dataentry/getOperationStrategicTasks?readingId=${readingId}&type=${type}`,
    });
  }
}
